import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_URL } from '../../utils/constants';
import { getAuthToken } from '../../utils/authToken';

export const getProductReviews = createAsyncThunk(
  'reviews/getProductReviews',
  async (productId, thunkAPI) => {
    try {
      const res = await axios.get(`${BASE_URL}/reviews/product/${productId}`);
      return { productId, ...res.data };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const createReview = createAsyncThunk(
  'reviews/createReview',
  async ({ productId, rating, text }, thunkAPI) => {
    try {
      const token = getAuthToken(thunkAPI.getState);
      if (!token) {
        return thunkAPI.rejectWithValue('Увійдіть в акаунт, щоб залишити відгук');
      }
      const res = await axios.post(`${BASE_URL}/reviews`, { productId, rating, text }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { productId, review: res.data };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const getPublicReviews = createAsyncThunk(
  'reviews/getPublicReviews',
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(`${BASE_URL}/reviews/public`);
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const getReviews = createAsyncThunk(
  'reviews/getReviews',
  async (_, thunkAPI) => {
    try {
      const token = getAuthToken(thunkAPI.getState);
      if (!token) {
        return thunkAPI.rejectWithValue('Увійдіть як адміністратор');
      }
      const res = await axios.get(`${BASE_URL}/reviews`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const replyToReview = createAsyncThunk(
  'reviews/replyToReview',
  async ({ id, adminReply }, thunkAPI) => {
    try {
      const token = getAuthToken(thunkAPI.getState);
      if (!token) {
        return thunkAPI.rejectWithValue('Увійдіть як адміністратор');
      }
      const res = await axios.post(`${BASE_URL}/reviews/${id}/reply`, { adminReply }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const toggleReviewPublished = createAsyncThunk(
  'reviews/toggleReviewPublished',
  async ({ id, isPublished }, thunkAPI) => {
    try {
      const token = getAuthToken(thunkAPI.getState);
      if (!token) {
        return thunkAPI.rejectWithValue('Увійдіть як адміністратор');
      }
      const res = await axios.post(`${BASE_URL}/reviews/${id}/publish`, { isPublished }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const deleteReview = createAsyncThunk(
  'reviews/deleteReview',
  async (id, thunkAPI) => {
    try {
      const token = getAuthToken(thunkAPI.getState);
      if (!token) {
        return thunkAPI.rejectWithValue('Увійдіть як адміністратор');
      }
      await axios.delete(`${BASE_URL}/reviews/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const reviewsSlice = createSlice({
  name: 'reviews',
  initialState: {
    list: [],
    homeReviews: [],
    homeReviewsLoading: false,
    byProduct: {},
    isLoading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProductReviews.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProductReviews.fulfilled, (state, action) => {
        state.isLoading = false;
        const { productId, reviews, count, averageRating } = action.payload;
        state.byProduct[productId] = { reviews, count, averageRating };
      })
      .addCase(getProductReviews.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createReview.fulfilled, (state, action) => {
        const { productId, review } = action.payload;
        const bucket = state.byProduct[productId] || { reviews: [], count: 0, averageRating: 0 };
        bucket.reviews = [review, ...bucket.reviews];
        bucket.count = bucket.reviews.length;
        const sum = bucket.reviews.reduce((acc, r) => acc + r.rating, 0);
        bucket.averageRating = Math.round((sum / bucket.count) * 10) / 10;
        state.byProduct[productId] = bucket;
      })
      .addCase(getPublicReviews.pending, (state) => {
        state.homeReviewsLoading = true;
      })
      .addCase(getPublicReviews.fulfilled, (state, action) => {
        state.homeReviewsLoading = false;
        state.homeReviews = action.payload;
      })
      .addCase(getPublicReviews.rejected, (state) => {
        state.homeReviewsLoading = false;
        state.homeReviews = [];
      })
      .addCase(getReviews.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getReviews.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = action.payload;
      })
      .addCase(getReviews.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(replyToReview.fulfilled, (state, action) => {
        const index = state.list.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(toggleReviewPublished.fulfilled, (state, action) => {
        const index = state.list.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(deleteReview.fulfilled, (state, action) => {
        state.list = state.list.filter((r) => r.id !== action.payload);
      });
  },
});

export default reviewsSlice.reducer;
